import { motion, AnimatePresence } from 'framer-motion';
import type { Lane, CardInstance, LaneDuelResult, CardDefinition, Faction } from '../../types/kotch';
import { LANE_REWARDS } from '../../types/kotch';
import Card from './Card';
import { LANE_NAMES } from '../../data/kotch/gameConstants';
import { FACTION_ABILITIES, FACTION_INFO } from '../../data/kotch/factionDecks';

interface LaneSlotProps {
  lane: Lane;
  player1Card: CardInstance | null;
  player2Card: CardInstance | null;
  revealed: boolean;
  result?: LaneDuelResult;
  viewingAsPlayer?: 1 | 2;
  player1Faction?: Faction | null;
  player2Faction?: Faction | null;
  onCardHover?: (def: CardDefinition | null) => void;
}

export default function LaneSlot({
  lane,
  player1Card,
  player2Card,
  revealed,
  result,
  viewingAsPlayer = 1,
  player1Faction,
  player2Faction,
  onCardHover,
}: LaneSlotProps) {
  const getOutcomeClass = () => {
    if (!result) return '';
    if (result.outcome === 'draw' || result.outcome === 'empty') return 'draw';
    if (result.winningPlayerId === viewingAsPlayer) return 'win';
    return 'lose';
  };

  const getOutcomeText = () => {
    if (!result) return '';
    if (result.outcome === 'empty') return 'Empty';
    if (result.outcome === 'draw') return 'Draw';
    if (result.outcome === 'unopposed_p1' || result.outcome === 'unopposed_p2') {
      return 'Unopposed';
    }
    if (result.winningPlayerId === viewingAsPlayer) return 'Victory';
    return 'Defeat';
  };

  const reward = LANE_REWARDS[lane];

  // Center lane: show two faction ability icons
  const renderCenterLaneReward = () => {
    const myFaction = player1Faction;
    const theirFaction = player2Faction;
    const myAbility = myFaction ? FACTION_ABILITIES[myFaction] : null;
    const theirAbility = theirFaction ? FACTION_ABILITIES[theirFaction] : null;
    const myInfo = myFaction ? FACTION_INFO[myFaction] : null;
    const theirInfo = theirFaction ? FACTION_INFO[theirFaction] : null;

    return (
      <div className="kotch-lane-faction-rewards">
        <div
          className="kotch-faction-reward-icon"
          style={myInfo ? { borderColor: myInfo.themeColor, color: myInfo.themeColor } : undefined}
          title={myAbility ? `${myInfo?.name}: ${myAbility.name} — ${myAbility.description}` : 'Faction ability'}
        >
          <span>{reward.icon}</span>
        </div>
        <div
          className="kotch-faction-reward-icon opponent"
          style={theirInfo ? { borderColor: theirInfo.themeColor, color: theirInfo.themeColor } : undefined}
          title={theirAbility ? `${theirInfo?.name}: ${theirAbility.name} — ${theirAbility.description}` : 'Faction ability'}
        >
          <span>{reward.icon}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="kotch-lane">
      <div className="kotch-lane-header">
        <span className="kotch-lane-name">{LANE_NAMES[lane]}</span>
        {lane === 'center' ? (
          renderCenterLaneReward()
        ) : (
          <div className="kotch-lane-reward" title={reward.description}>
            <span className="kotch-lane-reward-icon">{reward.icon}</span>
          </div>
        )}
      </div>

      {/* Opponent's card (player 2 if viewing as player 1) */}
      <div className={`kotch-lane-slot ${result ? getOutcomeClass() : ''}`}>
        <AnimatePresence mode="wait">
          {player2Card && (
            <motion.div
              key={player2Card.instanceId}
              initial={{ opacity: 0, rotateY: 180 }}
              animate={{ opacity: 1, rotateY: revealed ? 0 : 180 }}
              transition={{ duration: 0.4 }}
            >
              <Card
                card={player2Card}
                faceUp={revealed}
                size="medium"
                playerFaction={player2Faction}
                onHover={onCardHover}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Player's card (player 1 if viewing as player 1) */}
      <div className={`kotch-lane-slot ${result ? getOutcomeClass() : ''}`}>
        <AnimatePresence mode="wait">
          {player1Card && (
            <motion.div
              key={player1Card.instanceId}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card
                card={player1Card}
                faceUp={revealed}
                size="medium"
                playerFaction={player1Faction}
                onHover={onCardHover}
              />
            </motion.div>
          )}
        </AnimatePresence>
        {result && (
          <span className={`kotch-lane-result ${getOutcomeClass()}`}>
            {getOutcomeText()}
          </span>
        )}
      </div>
    </div>
  );
}
